import { useReducedMotion } from "../../hooks/useReducedMotion";

/**
 * Soft drifting orbs and rising bubbles behind a live board.
 *
 * Deliberately quiet: a retro board is a working surface, and anything that
 * competes with the notes is a bug. It sits behind the content, ignores the
 * pointer so dragging is unaffected, and disappears entirely for anyone who
 * has asked for reduced motion.
 *
 * The values are fixed rather than random so the board looks the same on every
 * render — and so screenshots and tests stay stable.
 */
const ORBS = [
  {
    className: "left-[-8%] top-[-10%] h-[26rem] w-[26rem] bg-lane-1/12 dark:bg-lane-1/25",
    animation: "driftA 34s ease-in-out infinite",
  },
  {
    className: "right-[-6%] top-[8%] h-[22rem] w-[22rem] bg-lane-2/10 dark:bg-lane-2/22",
    animation: "driftB 42s ease-in-out infinite",
  },
  {
    className: "bottom-[-12%] left-[22%] h-[24rem] w-[24rem] bg-lane-3/10 dark:bg-lane-3/20",
    animation: "driftC 38s ease-in-out infinite 3s",
  },
  {
    className: "bottom-[6%] right-[14%] h-[18rem] w-[18rem] bg-lane-4/9 dark:bg-lane-4/18",
    animation: "driftA 46s ease-in-out infinite 6s",
  },
];

const BUBBLES = [
  { left: "6%", size: 10, duration: 26, delay: 0, drift: "3vw" },
  { left: "19%", size: 6, duration: 32, delay: 5, drift: "-2vw" },
  { left: "34%", size: 14, duration: 29, delay: 11, drift: "2vw" },
  { left: "48%", size: 8, duration: 36, delay: 3, drift: "-3vw" },
  { left: "62%", size: 11, duration: 24, delay: 8, drift: "2vw" },
  { left: "77%", size: 7, duration: 34, delay: 14, drift: "-2vw" },
  { left: "89%", size: 12, duration: 30, delay: 6, drift: "3vw" },
];

export function BoardAmbience() {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="board-ambience"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {ORBS.map((orb, index) => (
        <span
          key={index}
          style={{ animation: orb.animation, willChange: "transform" }}
          className={`absolute rounded-full blur-3xl ${orb.className}`}
        />
      ))}

      {BUBBLES.map((bubble, index) => (
        <span
          key={index}
          style={
            {
              left: bubble.left,
              width: bubble.size,
              height: bubble.size,
              "--drift": bubble.drift,
              animation: `rise ${bubble.duration}s linear infinite ${bubble.delay}s`,
              willChange: "transform, opacity",
            } as React.CSSProperties
          }
          className="absolute bottom-0 rounded-full bg-nm-accent/35 dark:bg-nm-accent/40"
        />
      ))}
    </div>
  );
}
